import React, { useState, useEffect, useCallback } from "react";
import { Layout } from "@/components/layout";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/auth-provider";
import { useCurrentBusiness } from "@/hooks/use-current-business";
import { useLoading } from "@/providers/loading-provider";
import { useInvoices, Invoice, CreateInvoiceData } from "@/hooks/use-invoices";
import { apiRequest } from "@/lib/queryClient";
import { useCustomers } from "@/hooks/use-customers";
import { useProducts } from "@/hooks/use-products";
import { useLocation, Link } from "wouter";
import { format } from "date-fns";

function safeFormatDate(date: any, fmt = "MMM d, yyyy"): string {
  if (!date) return "-";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "-";
  return format(d, fmt);
}
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Full QuickBooks-style invoice PDF generator
async function generateInvoicePDF(invoice: any, business: any, customers: any[]) {
  // Fetch items if not already loaded
  let items = invoice.items || [];
  if (items.length === 0 && invoice.id && business?.id) {
    try {
      const res = await fetch(`/api/businesses/${business.id}/invoices/${invoice.id}`, { credentials: "include" });
      if (res.ok) {
        const full = await res.json();
        items = full.items || [];
      }
    } catch { /* use empty */ }
  }

  // Fetch full business details (address, phone, etc.)
  let biz = business;
  if (business?.id) {
    try {
      const bizRes = await fetch(`/api/businesses/${business.id}`, { credentials: "include" });
      if (bizRes.ok) biz = await bizRes.json();
    } catch { /* use passed business */ }
  }

  const customer = customers?.find((c: any) =>
    c.id === (invoice.customerId || invoice.customer_id)
  );

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header
  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  doc.text("INVOICE", pageWidth - 20, 25, { align: "right" });

  // Invoice # and dates (top left)
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Invoice #: ${invoice.invoiceNumber || invoice.invoice_number || "-"}`, 20, 25);
  doc.text(`Date: ${safeFormatDate(invoice.issueDate || invoice.date, "MM/dd/yyyy")}`, 20, 31);
  doc.text(`Due Date: ${safeFormatDate(invoice.dueDate || invoice.due_date, "MM/dd/yyyy")}`, 20, 37);
  doc.text(`Status: ${(invoice.status || "draft").toUpperCase()}`, 20, 43);

  // From (business) — left column
  let y = 58;
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text("FROM", 20, y);
  doc.setTextColor(0);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text(biz?.name || "Your Business", 20, y + 6);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const bizAddr = biz?.address || "";
  if (bizAddr) doc.text(bizAddr, 20, y + 12);
  if (biz?.email) doc.text(biz.email, 20, y + (bizAddr ? 17 : 12));
  const bizPhone = biz?.phone_number || biz?.phoneNumber || biz?.phone;
  if (bizPhone) doc.text(bizPhone, 20, y + (bizAddr ? 22 : 17));

  // To (customer) — right column
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text("BILL TO", 120, y);
  doc.setTextColor(0);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text(customer?.name || invoice.customerName || invoice.customer_name || "Customer", 120, y + 6);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const custAddr = [
    customer?.street, customer?.city, customer?.state, customer?.zip_code || customer?.zipCode
  ].filter(Boolean).join(", ");
  if (custAddr) doc.text(custAddr, 120, y + 12);
  if (customer?.email) doc.text(customer.email, 120, y + (custAddr ? 17 : 12));
  if (customer?.phone) doc.text(customer.phone, 120, y + (custAddr ? 22 : 17));

  // Items table
  const tableBody = items.map((item: any, i: number) => {
    const qty = Number(item.quantity) || 1;
    const price = Number(item.unitPrice || item.unit_price || item.price) || 0;
    const rate = Number(item.taxRate || item.tax_rate) || 0;
    const lineTotal = Number(item.amount) || qty * price;
    return [
      String(i + 1),
      item.description || item.name || "-",
      String(qty),
      `$${price.toFixed(2)}`,
      rate > 0 ? `${rate}%` : "-",
      `$${lineTotal.toFixed(2)}`,
    ];
  });

  autoTable(doc, {
    startY: 100,
    head: [["#", "Description", "Qty", "Unit Price", "Tax", "Amount"]],
    body: tableBody.length > 0 ? tableBody : [["", "No items", "", "", "", ""]],
    theme: "grid",
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: "bold", fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 12, halign: "center" },
      2: { cellWidth: 18, halign: "center" },
      3: { cellWidth: 28, halign: "right" },
      4: { cellWidth: 18, halign: "center" },
      5: { cellWidth: 28, halign: "right" },
    },
  });

  // Totals
  const finalY = (doc as any).lastAutoTable?.finalY || 150;
  doc.setFontSize(10);
  doc.text("Subtotal:", 140, finalY + 10);
  doc.text(`$${Number(invoice.subtotal || 0).toFixed(2)}`, pageWidth - 20, finalY + 10, { align: "right" });

  doc.text("Tax:", 140, finalY + 16);
  doc.text(`$${Number(invoice.taxAmount || invoice.tax_amount || 0).toFixed(2)}`, pageWidth - 20, finalY + 16, { align: "right" });

  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.text("Total:", 140, finalY + 26);
  doc.text(`$${Number(invoice.total || 0).toFixed(2)}`, pageWidth - 20, finalY + 26, { align: "right" });

  // Amount paid / balance due
  const paid = Number(invoice.amountPaid || invoice.amount_paid || 0);
  if (paid > 0) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Amount Paid:", 140, finalY + 34);
    doc.text(`$${paid.toFixed(2)}`, pageWidth - 20, finalY + 34, { align: "right" });
    const balance = Number(invoice.total || 0) - paid;
    doc.setFont("helvetica", "bold");
    doc.text("Balance Due:", 140, finalY + 42);
    doc.text(`$${balance.toFixed(2)}`, pageWidth - 20, finalY + 42, { align: "right" });
  }

  // PAID watermark
  if (invoice.status === "paid") {
    doc.setFontSize(60);
    doc.setTextColor(0, 180, 0);
    doc.setFont("helvetica", "bold");
    doc.text("PAID", pageWidth / 2, 160, { align: "center", angle: 45 } as any);
    doc.setTextColor(0);
  }

  // Notes & Terms
  let notesY = finalY + (paid > 0 ? 55 : 40);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  if (invoice.notes) {
    doc.setFont("helvetica", "bold");
    doc.text("Notes:", 20, notesY);
    doc.setFont("helvetica", "normal");
    doc.text(invoice.notes, 20, notesY + 5);
    notesY += 15;
  }
  if (invoice.terms) {
    doc.setFont("helvetica", "bold");
    doc.text("Terms:", 20, notesY);
    doc.setFont("helvetica", "normal");
    doc.text(invoice.terms, 20, notesY + 5);
  }

  doc.save(`Invoice-${invoice.invoiceNumber || invoice.invoice_number || invoice.id}.pdf`);
}
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { InvoicePaymentLink } from "@/components/sales/invoice-payment-link";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, 
  Plus, 
  RefreshCw, 
  Trash2, 
  MoreHorizontal, 
  Loader2, 
  DollarSign, 
  Calendar, 
  Clock,
  Edit,
  Send,
  CheckCircle,
  AlertCircle,
  Download,
  Copy,
  Mail
} from "lucide-react";

// Create invoice form schema
const invoiceFormSchema = z.object({
  customerId: z.string().min(1, "Customer is required"),
  issueDate: z.string().min(1, "Issue date is required"),
  dueDate: z.string().min(1, "Due date is required"),
  status: z.enum(["draft", "sent", "paid", "overdue", "cancelled"]).default("draft"),
  notes: z.string().optional(),
  terms: z.string().optional(),
  items: z.array(
    z.object({
      productId: z.string().optional(),
      description: z.string().min(1, "Description is required"),
      quantity: z.number().min(0.01, "Quantity must be greater than 0"),
      unitPrice: z.number().min(0, "Unit price must be positive"),
      taxRate: z.number().min(0, "Tax rate must be positive").max(100, "Tax rate cannot exceed 100%").default(0),
    })
  ).min(1, "At least one item is required"),
});

type InvoiceFormValues = z.infer<typeof invoiceFormSchema>;

interface InvoicesPageProps {
  customerId?: string;
}

export default function InvoicesPage({ customerId }: InvoicesPageProps = {}) {
  const { toast } = useToast();
  const { currentBusiness } = useCurrentBusiness();
  const { navigateTo } = useLoading();
  const [location, navigate] = useLocation();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [currentTab, setCurrentTab] = useState("all");
  
  // Fetch invoices
  const { 
    invoices, 
    isLoading, 
    error, 
    refetch,
    createInvoiceMutation,
    updateInvoiceMutation,
    updateInvoiceStatusMutation,
    deleteInvoiceMutation
  } = useInvoices();
  
  // Fetch customers for dropdown
  const { customers } = useCustomers();
  
  // Fetch products for dropdown
  const { products } = useProducts();
  
  // Auto-open create invoice dialog when coming from customer details
  useEffect(() => {
    // If customerId is provided, open the create dialog automatically
    if (customerId) {
      setIsCreateDialogOpen(true);
    }
  }, [customerId]);
  
  // Check for overdue invoices and update their status if needed
  const checkForOverdueInvoices = useCallback(() => {
    if (!invoices) return;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time part for date comparison
    
    invoices.forEach((invoice: Invoice) => {
      // Only check invoices that are in 'sent' status
      if (invoice.status === 'sent') {
        const dueDate = new Date(invoice.dueDate);
        dueDate.setHours(0, 0, 0, 0); // Reset time part for date comparison
        
        // If due date has passed and status is still 'sent', update to 'overdue'
        if (dueDate < today) {
          console.log(`Invoice ${invoice.id} is overdue. Due date: ${invoice.dueDate}, Today: ${today.toISOString()}`);
          updateInvoiceStatusMutation.mutate({
            id: invoice.id,
            status: 'overdue'
          });
        }
      }
    });
  }, [invoices, updateInvoiceStatusMutation]);
  
  // Run the overdue check when invoices are loaded or changed
  useEffect(() => {
    checkForOverdueInvoices();
  }, [invoices, checkForOverdueInvoices]);

  // Filter invoices by status for tabs
  const filteredInvoices = invoices?.filter((invoice: Invoice) => {
    if (currentTab === "all") return true;
    return invoice.status === currentTab;
  });
  
  // Form setup for creating invoices
  const form = useForm<InvoiceFormValues>({
    resolver: zodResolver(invoiceFormSchema),
    defaultValues: {
      customerId: "",
      issueDate: format(new Date(), "yyyy-MM-dd"),
      dueDate: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"), // Default due date: 30 days from now
      status: "draft",
      notes: "",
      terms: "Payment is due within 30 days of issue.",
      items: [
        {
          productId: undefined,
          description: "",
          quantity: 1,
          unitPrice: 0,
          taxRate: 0,
        },
      ],
    },
  });
  
  // Field array for invoice items
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "items",
  });
  
  // Calculate totals
  const calculateTotals = () => {
    const items = form.getValues("items");
    let subtotal = 0;
    let taxTotal = 0;
    
    items.forEach(item => {
      const lineTotal = item.quantity * item.unitPrice;
      subtotal += lineTotal;
      taxTotal += lineTotal * (item.taxRate / 100);
    });
    
    const total = subtotal + taxTotal;
    
    return { subtotal, taxTotal, total };
  };
  
  // Prefill product details when a product is selected
  const handleProductChange = (index: number, productId: string) => {
    if (!productId) return;
    
    const selectedProduct = products?.find((p: any) => p.id.toString() === productId);
    if (selectedProduct) {
      form.setValue(`items.${index}.description`, selectedProduct.name);
      form.setValue(`items.${index}.unitPrice`, parseFloat(selectedProduct.price));
      form.setValue(`items.${index}.taxRate`, parseFloat(selectedProduct.taxRate || "0"));
    }
  };
  
  // Handle form submission with status option
  const onSubmit = (data: InvoiceFormValues, status: 'draft' | 'sent' = 'draft') => {
    // Convert form data to match API expectations
    const invoiceData: CreateInvoiceData = {
      customerId: parseInt(data.customerId),
      issueDate: data.issueDate,
      dueDate: data.dueDate,
      status: status, // Use the provided status
      notes: data.notes,
      terms: data.terms,
      items: data.items.map(item => ({
        productId: item.productId ? parseInt(item.productId) : undefined,
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        taxRate: item.taxRate,
      })),
    };
    
    createInvoiceMutation.mutate(invoiceData, {
      onSuccess: () => {
        toast({
          title: "Success",
          description: status === 'draft' 
            ? "Invoice saved as draft" 
            : "Invoice created and marked as sent",
        });
        setIsCreateDialogOpen(false);
        form.reset();
      }
    });
  };
  
  // Format currency display
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Get status badge variant and custom styles
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft': 
        return { variant: 'secondary' as const, className: "bg-gray-200 text-gray-800 hover:bg-gray-300" };
      case 'sent': 
        return { variant: 'default' as const, className: "bg-blue-500 text-white hover:bg-blue-600" };
      case 'paid': 
        return { variant: 'default' as const, className: "bg-green-500 text-white hover:bg-green-600" };
      case 'overdue': 
        return { variant: 'destructive' as const, className: "bg-red-500 text-white hover:bg-red-600" };
      case 'cancelled': 
        return { variant: 'outline' as const, className: "border-gray-400 text-gray-800" };
      default: 
        return { variant: 'secondary' as const, className: "bg-gray-200 text-gray-800 hover:bg-gray-300" };
    }
  };
  
  // Function to handle invoice creation
  const handleCreateInvoice = (data: InvoiceFormValues) => {
    // Convert form data to match API expectations
    const invoiceData: CreateInvoiceData = {
      customerId: parseInt(data.customerId),
      issueDate: data.issueDate,
      dueDate: data.dueDate,
      status: 'draft', // Default to draft 
      notes: data.notes,
      terms: data.terms,
      items: data.items.map(item => ({
        productId: item.productId ? parseInt(item.productId) : undefined,
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        taxRate: item.taxRate,
      })),
    };
    
    createInvoiceMutation.mutate(invoiceData, {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Invoice saved as draft",
        });
        setIsCreateDialogOpen(false);
        form.reset();
      }
    });
  };
  
  // Function to handle invoice editing
  const handleEditInvoice = async (data: InvoiceFormValues) => {
    if (!selectedInvoice) return;
    
    // Convert form data to match API expectations
    const invoiceData: Partial<CreateInvoiceData> = {
      customerId: parseInt(data.customerId),
      issueDate: data.issueDate,
      dueDate: data.dueDate,
      status: data.status,
      notes: data.notes,
      terms: data.terms,
      items: data.items.map(item => ({
        productId: item.productId ? parseInt(item.productId) : undefined,
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        taxRate: item.taxRate,
      })),
    };
    
    return new Promise<void>((resolve, reject) => {
      updateInvoiceMutation.mutate(
        { id: selectedInvoice.id, data: invoiceData },
        {
          onSuccess: () => {
            toast({
              title: "Success",
              description: "Invoice updated successfully",
            });
            setIsEditDialogOpen(false);
            resolve();
          },
          onError: (error) => {
            toast({
              title: "Error",
              description: "Failed to update invoice: " + error.message,
              variant: "destructive"
            });
            reject(error);
          }
        }
      );
    });
  };

  // Reset form when dialog closes
  useEffect(() => {
    if (!isCreateDialogOpen) {
      form.reset({
        customerId: "",
        issueDate: format(new Date(), "yyyy-MM-dd"),
        dueDate: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"),
        status: "draft",
        notes: "",
        terms: "Payment is due within 30 days of issue.",
        items: [
          {
            productId: undefined,
            description: "",
            quantity: 1,
            unitPrice: 0,
            taxRate: 0,
          },
        ],
      });
    }
  }, [isCreateDialogOpen, form]);
  
  // Pre-select customer when customerId is provided from a customer details page
  useEffect(() => {
    if (customerId && isCreateDialogOpen) {
      // Set the customerId in the form
      form.setValue("customerId", customerId);
    }
  }, [customerId, isCreateDialogOpen, form]);
  
  // Load selected invoice data into form when editing
  useEffect(() => {
    if (selectedInvoice && isEditDialogOpen) {
      console.log("Selected invoice:", selectedInvoice);
      console.log("Selected invoice items:", selectedInvoice.items);
      
      // Ensure dates are properly formatted for the form
      const issueDate = safeFormatDate(selectedInvoice.issueDate || selectedInvoice.date, "yyyy-MM-dd") !== "-" ? safeFormatDate(selectedInvoice.issueDate || selectedInvoice.date, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd");
      
      // Make sure due date is not in the past
      let dueDate = safeFormatDate(selectedInvoice.dueDate || selectedInvoice.due_date, "yyyy-MM-dd") !== "-" ? safeFormatDate(selectedInvoice.dueDate || selectedInvoice.due_date, "yyyy-MM-dd") : format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd");
      const today = format(new Date(), "yyyy-MM-dd");
      if (dueDate < today) {
        dueDate = today;
      }
      
      if (!selectedInvoice.items || selectedInvoice.items.length === 0) {
        // Create a placeholder item if there are no items
        const items = [{
          productId: undefined,
          description: "Item description",
          quantity: 1,
          unitPrice: 0,
          taxRate: 0,
        }];
        
        // Reset form with selected invoice data
        form.reset({
          customerId: selectedInvoice.customerId.toString(),
          issueDate: issueDate,
          dueDate: dueDate,
          status: selectedInvoice.status,
          notes: selectedInvoice.notes || "",
          terms: selectedInvoice.terms || "",
          items: items,
        });
      } else {
        // Reset form with selected invoice data including items
        form.reset({
          customerId: selectedInvoice.customerId.toString(),
          issueDate: issueDate,
          dueDate: dueDate,
          status: selectedInvoice.status,
          notes: selectedInvoice.notes || "",
          terms: selectedInvoice.terms || "",
          items: selectedInvoice.items?.map(item => ({
            productId: item.productId ? item.productId.toString() : undefined,
            description: item.description,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            taxRate: item.taxRate,
          })) || [],
        });
      }
    }
  }, [selectedInvoice, isEditDialogOpen, form]);

  if (!currentBusiness) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <h2 className="text-2xl font-bold mb-4">No Business Selected</h2>
          <p className="text-muted-foreground mb-6">
            Please select a business to manage invoices
          </p>
        </div>
      </Layout>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto py-6">
          <div className="flex justify-center items-center h-[60vh]">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (error) {
    return (
      <Layout>
        <div className="container mx-auto py-6">
          <div className="flex flex-col items-center justify-center h-[60vh]">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h2 className="text-2xl font-bold mb-2">Failed to Load Invoices</h2>
            <p className="text-muted-foreground mb-4">
              {(error as Error).message || "An unexpected error occurred"}
            </p>
            <Button onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" /> Try Again
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto py-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Invoices</h1>
            <p className="text-muted-foreground">
              Create and manage customer invoices
            </p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                New Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>Create New Invoice</DialogTitle>
                <DialogDescription>
                  Create a new invoice for your customer.
                </DialogDescription>
              </DialogHeader>
              
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleCreateInvoice)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Customer Selection */}
                    <FormField
                      control={form.control}
                      name="customerId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Customer</FormLabel>
                          <Select 
                            onValueChange={field.onChange} 
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a customer" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {customers && customers.length > 0 ? (
                                customers.map(customer => (
                                  <SelectItem key={customer.id} value={customer.id.toString()}>
                                    {customer.name}
                                  </SelectItem>
                                ))
                              ) : (
                                <div className="flex flex-col items-center p-4 text-center">
                                  <p className="text-sm text-muted-foreground mb-2">No customers available</p>
                                  <Button 
                                    size="sm" 
                                    className="w-full"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setIsCreateDialogOpen(false);
                                                                          // Navigate to customers page to create a new customer
                                      window.location.href = "/sales/customers";
                                    }}
                                  >
                                    Create Customer
                                  </Button>
                                </div>
                              )}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    {/* Issue Date */}
                    <FormField
                      control={form.control}
                      name="issueDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Issue Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    {/* Due Date */}
                    <FormField
                      control={form.control}
                      name="dueDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Due Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  {/* Invoice Items */}
                  <div className="border rounded-md p-4">
                    <h3 className="font-medium mb-3">Invoice Items</h3>
                    <div className="space-y-4">
                      {fields.map((item, index) => (
                        <div key={item.id} className="grid grid-cols-12 gap-2 items-start">
                          {/* Product Selection */}
                          <div className="col-span-12 md:col-span-3">
                            <FormField
                              control={form.control}
                              name={`items.${index}.productId`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-xs">Product</FormLabel>
                                  <Select 
                                    onValueChange={(value) => {
                                      field.onChange(value);
                                      handleProductChange(index, value);
                                    }} 
                                    value={field.value}
                                  >
                                    <FormControl>
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select or leave empty" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      {products?.filter(product => product.isActive !== false).length === 0 ? (
                                        <div className="flex flex-col items-center justify-center p-4 space-y-2">
                                          <p className="text-sm text-center text-muted-foreground">No active products available</p>
                                          <Link href="/sales/products">
                                            <Button size="sm" variant="outline" className="w-full">
                                              <Plus className="h-4 w-4 mr-2" />
                                              Create Product
                                            </Button>
                                          </Link>
                                        </div>
                                      ) : (
                                        products?.filter(product => product.isActive !== false)
                                          .map(product => (
                                            <SelectItem key={product.id} value={product.id.toString()}>
                                              {product.name}
                                            </SelectItem>
                                          ))
                                      )}
                                    </SelectContent>
                                  </Select>
                                </FormItem>
                              )}
                            />
                          </div>
                          
                          {/* Description */}
                          <div className="col-span-12 md:col-span-3">
                            <FormField
                              control={form.control}
                              name={`items.${index}.description`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-xs">Description</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          
                          {/* Quantity */}
                          <div className="col-span-3 md:col-span-1">
                            <FormField
                              control={form.control}
                              name={`items.${index}.quantity`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-xs">Qty</FormLabel>
                                  <FormControl>
                                    <Input 
                                      type="number" 
                                      min="0" 
                                      step="0.01" 
                                      {...field} 
                                      onChange={e => field.onChange(parseFloat(e.target.value))}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          
                          {/* Unit Price */}
                          <div className="col-span-3 md:col-span-2">
                            <FormField
                              control={form.control}
                              name={`items.${index}.unitPrice`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-xs">Price</FormLabel>
                                  <FormControl>
                                    <Input 
                                      type="number" 
                                      min="0" 
                                      step="0.01" 
                                      {...field} 
                                      onChange={e => field.onChange(parseFloat(e.target.value))}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          
                          {/* Tax Rate */}
                          <div className="col-span-3 md:col-span-2">
                            <FormField
                              control={form.control}
                              name={`items.${index}.taxRate`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-xs">Tax Rate %</FormLabel>
                                  <FormControl>
                                    <Input 
                                      type="number" 
                                      min="0" 
                                      max="100" 
                                      step="0.01" 
                                      {...field} 
                                      onChange={e => field.onChange(parseFloat(e.target.value))}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          
                          {/* Remove Button */}
                          <div className="col-span-3 md:col-span-1 pt-7">
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => remove(index)}
                              disabled={fields.length === 1}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      
                      {/* Add Item Button */}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => append({
                          productId: undefined,
                          description: "",
                          quantity: 1,
                          unitPrice: 0,
                          taxRate: 0,
                        })}
                      >
                        <Plus className="h-4 w-4 mr-2" /> Add Item
                      </Button>
                    </div>
                  </div>
                  
                  {/* Totals Section */}
                  <div className="flex justify-end">
                    <div className="w-full max-w-xs space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Subtotal:</span>
                        <span>{formatCurrency(calculateTotals().subtotal)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tax:</span>
                        <span>{formatCurrency(calculateTotals().taxTotal)}</span>
                      </div>
                      <div className="flex justify-between font-bold">
                        <span>Total:</span>
                        <span>{formatCurrency(calculateTotals().total)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Notes and Terms */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="notes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Notes</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Additional notes for the customer"
                              className="h-20"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="terms"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Terms & Conditions</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Invoice terms and conditions"
                              className="h-20"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <DialogFooter className="flex justify-between items-center">
                    <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <div className="flex gap-2">
                      <Button 
                        type="button" 
                        variant="secondary" 
                        disabled={createInvoiceMutation.isPending}
                        onClick={async () => {
                          // Validate form first
                          const isValid = await form.trigger();
                          if (isValid) {
                            const values = form.getValues();
                            onSubmit(values, 'draft');
                          }
                        }}
                      >
                        {createInvoiceMutation.isPending && form.getValues().status === 'draft' && 
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        }
                        Save as Draft
                      </Button>
                      <Button 
                        type="button" 
                        variant="default" 
                        disabled={createInvoiceMutation.isPending}
                        onClick={async () => {
                          // Validate form first
                          const isValid = await form.trigger();
                          if (isValid) {
                            const values = form.getValues();
                            
                            // Convert form data to match API expectations
                            const invoiceData: CreateInvoiceData = {
                              customerId: parseInt(values.customerId),
                              issueDate: values.issueDate,
                              dueDate: values.dueDate,
                              status: 'sent', // Mark as sent immediately
                              notes: values.notes,
                              terms: values.terms,
                              items: values.items.map(item => ({
                                productId: item.productId ? parseInt(item.productId) : undefined,
                                description: item.description,
                                quantity: item.quantity,
                                unitPrice: item.unitPrice,
                                taxRate: item.taxRate,
                              })),
                            };
                            
                            createInvoiceMutation.mutate(invoiceData, {
                              onSuccess: () => {
                                toast({
                                  title: "Success",
                                  description: "Invoice created and sent successfully",
                                });
                                setIsCreateDialogOpen(false);
                                form.reset();
                              }
                            });
                          }
                        }}
                      >
                        {createInvoiceMutation.isPending && form.getValues().status === 'sent' && 
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        }
                        Send Invoice
                      </Button>
                    </div>
                  </DialogFooter>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs value={currentTab} onValueChange={setCurrentTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="draft">Draft</TabsTrigger>
            <TabsTrigger value="sent">Sent</TabsTrigger>
            <TabsTrigger value="paid">Paid</TabsTrigger>
            <TabsTrigger value="overdue">Overdue</TabsTrigger>
          </TabsList>

          <TabsContent value={currentTab} className="space-y-4">
            {/* Invoices Table */}
            {filteredInvoices && filteredInvoices.length > 0 ? (
              <Card>
                <CardContent className="p-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInvoices.map((invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell className="font-medium">{invoice.invoiceNumber}</TableCell>
                          <TableCell>{invoice.customerName}</TableCell>
                          <TableCell>{safeFormatDate(invoice.issueDate || invoice.date)}</TableCell>
                          <TableCell>{safeFormatDate(invoice.dueDate || invoice.due_date)}</TableCell>
                          <TableCell>
                            {(() => {
                              const badgeStyle = getStatusBadge(invoice.status);
                              return (
                                <Badge variant={badgeStyle.variant} className={`text-sm px-3 py-1 ${badgeStyle.className}`}>
                                  {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                                </Badge>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(invoice.total)}</TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                <DropdownMenuItem 
                                  onClick={async () => {
                                    // First fetch the complete invoice with items
                                    try {
                                      const res = await apiRequest(
                                        "GET", 
                                        `/api/businesses/${currentBusiness?.id}/invoices/${invoice.id}`
                                      );
                                      if (!res.ok) {
                                        throw new Error("Failed to fetch invoice details");
                                      }
                                      const completeInvoice = await res.json();
                                      console.log("Fetched complete invoice for view:", completeInvoice);
                                      setSelectedInvoice(completeInvoice);
                                      setIsViewDialogOpen(true);
                                    } catch (error) {
                                      console.error("Error fetching invoice details:", error);
                                      toast({
                                        title: "Error",
                                        description: "Failed to load invoice details",
                                        variant: "destructive"
                                      });
                                    }
                                  }}
                                >
                                  <FileText className="h-4 w-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={async () => {
                                    // Check if invoice is paid - if so, don't allow editing
                                    if (invoice.status === "paid") {
                                      toast({
                                        title: "Cannot Edit",
                                        description: "Paid invoices cannot be edited.",
                                        variant: "destructive"
                                      });
                                      return;
                                    }
                                    
                                    // First fetch the complete invoice with items
                                    try {
                                      const res = await apiRequest(
                                        "GET", 
                                        `/api/businesses/${currentBusiness?.id}/invoices/${invoice.id}`
                                      );
                                      if (!res.ok) {
                                        throw new Error("Failed to fetch invoice details");
                                      }
                                      const completeInvoice = await res.json();
                                      console.log("Fetched complete invoice:", completeInvoice);
                                      setSelectedInvoice(completeInvoice);
                                      setIsEditDialogOpen(true);
                                    } catch (error) {
                                      console.error("Error fetching invoice details:", error);
                                      toast({
                                        title: "Error",
                                        description: "Failed to load invoice details",
                                        variant: "destructive"
                                      });
                                    }
                                  }}
                                  disabled={invoice.status === "paid"}
                                  className={invoice.status === "paid" ? "text-muted-foreground cursor-not-allowed" : ""}
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {invoice.status === 'draft' && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      // Handle mark as sent
                                      updateInvoiceStatusMutation.mutate({
                                        id: invoice.id,
                                        status: 'sent'
                                      });
                                    }}
                                  >
                                    <Send className="h-4 w-4 mr-2" />
                                    Mark as Sent
                                  </DropdownMenuItem>
                                )}
                                {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      // Handle mark as paid
                                      updateInvoiceStatusMutation.mutate({
                                        id: invoice.id,
                                        status: 'paid'
                                      });
                                    }}
                                  >
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Mark as Paid
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={async () => {
                                    if (!invoice) return;
                                    try {
                                      await generateInvoicePDF(invoice, currentBusiness, customers || []);
                                      toast({ title: "PDF Generated", description: `Invoice ${invoice.invoiceNumber} downloaded` });
                                    } catch (error) {
                                      console.error("PDF generation error:", error);
                                      toast({ title: "PDF Generation Failed", description: "Error generating PDF", variant: "destructive" });
                                    }
                                  }}
                                >
                                  <Download className="h-4 w-4 mr-2" />
                                  Download PDF
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                      <Trash2 className="h-4 w-4 mr-2 text-destructive" />
                                      <span className="text-destructive">Delete</span>
                                    </DropdownMenuItem>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This action cannot be undone. This will permanently delete the
                                        invoice and remove it from our servers.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => {
                                          deleteInvoiceMutation.mutate(invoice.id);
                                        }}
                                      >
                                        Delete
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-6">
                  <div className="text-center py-10">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Invoices Yet</h3>
                    <p className="text-muted-foreground mb-4">
                      {currentTab === "all"
                        ? "You haven't created any invoices yet. Create your first invoice to get started."
                        : `You don't have any ${currentTab} invoices.`}
                    </p>
                    <Button onClick={() => setIsCreateDialogOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Invoice
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
        
        {/* View Invoice Dialog */}
        {selectedInvoice && (
          <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>Invoice {selectedInvoice.invoiceNumber}</DialogTitle>
                <DialogDescription>
                  <div className="flex items-center gap-2 mt-1">
                    {(() => {
                      const badgeStyle = getStatusBadge(selectedInvoice.status);
                      return (
                        <Badge variant={badgeStyle.variant} className={`text-sm px-3 py-1 ${badgeStyle.className}`}>
                          {selectedInvoice.status.charAt(0).toUpperCase() + selectedInvoice.status.slice(1)}
                        </Badge>
                      );
                    })()}
                    <span className="text-sm text-muted-foreground">
                      Created on {safeFormatDate(selectedInvoice.issueDate || selectedInvoice.date, 'MMMM d, yyyy')}
                    </span>
                  </div>
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium mb-1">Bill To:</h3>
                    <p className="text-base font-semibold">
                      {customers?.find(c => c.id === selectedInvoice.customerId)?.name || "Customer"}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center text-sm">
                      <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span className="text-muted-foreground mr-2">Issue Date:</span>
                      <span>{safeFormatDate(selectedInvoice.issueDate || selectedInvoice.date, 'MMMM d, yyyy')}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span className="text-muted-foreground mr-2">Due Date:</span>
                      <span>{safeFormatDate(selectedInvoice.dueDate || selectedInvoice.due_date, 'MMMM d, yyyy')}</span>
                    </div>
                  </div>
                </div>
                
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Tax Rate</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {/* We currently don't have invoice items in our invoice object */}
                      {/* This would normally display invoice line items */}
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-4 text-muted-foreground">
                          Invoice items not available in this view
                        </TableCell>
                      </TableRow>
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={4} className="text-right">Subtotal</TableCell>
                        <TableCell className="text-right">{formatCurrency(selectedInvoice.subtotal)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={4} className="text-right">Tax</TableCell>
                        <TableCell className="text-right">{formatCurrency(selectedInvoice.taxAmount)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={4} className="text-right font-bold">Total</TableCell>
                        <TableCell className="text-right font-bold">{formatCurrency(selectedInvoice.total)}</TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>
                
                {(selectedInvoice.notes || selectedInvoice.terms) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedInvoice.notes && (
                      <div>
                        <h3 className="text-sm font-medium mb-1">Notes:</h3>
                        <p className="text-sm text-muted-foreground">{selectedInvoice.notes}</p>
                      </div>
                    )}
                    {selectedInvoice.terms && (
                      <div>
                        <h3 className="text-sm font-medium mb-1">Terms & Conditions:</h3>
                        <p className="text-sm text-muted-foreground">{selectedInvoice.terms}</p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Payment Link Component removed to prevent BusinessProvider errors */}
                {/* We'll need to create a proper wrapper component with BusinessProvider context */}
              </div>
              
              <DialogFooter className="gap-2">
                <div className="flex flex-wrap gap-2 justify-end">
                  {selectedInvoice.status === 'draft' && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        updateInvoiceMutation.mutate({
                          id: selectedInvoice.id,
                          data: { status: 'sent' }
                        });
                        setIsViewDialogOpen(false);
                      }}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Mark as Sent
                    </Button>
                  )}
                  {(selectedInvoice.status === 'sent' || selectedInvoice.status === 'overdue') && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        updateInvoiceMutation.mutate({
                          id: selectedInvoice.id,
                          data: { status: 'paid' }
                        });
                        setIsViewDialogOpen(false);
                      }}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Mark as Paid
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => {
                      toast({
                        title: "Feature in progress",
                        description: "Email sending will be available soon",
                      });
                    }}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Send Email
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      if (!selectedInvoice) return;

                      try {
                        await generateInvoicePDF(selectedInvoice, currentBusiness, customers || []);
                        toast({ title: "PDF Generated", description: "Invoice downloaded successfully" });
                      } catch (error) {
                        console.error("PDF generation error:", error);
                        toast({ title: "PDF Generation Failed", description: "Error generating PDF", variant: "destructive" });
                      }
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        
        {/* Edit Invoice Dialog */}
        {selectedInvoice && (
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>Edit Invoice #{selectedInvoice.invoiceNumber}</DialogTitle>
                <DialogDescription>
                  Update the invoice details and save your changes.
                </DialogDescription>
              </DialogHeader>
              
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleEditInvoice)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="customerId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Customer</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value.toString()}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select customer" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {customers?.map((customer) => (
                                <SelectItem key={customer.id} value={customer.id.toString()}>
                                  {customer.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="draft">Draft</SelectItem>
                              <SelectItem value="sent">Sent</SelectItem>
                              <SelectItem value="paid">Paid</SelectItem>
                              <SelectItem value="overdue">Overdue</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="issueDate"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Issue Date</FormLabel>
                          <FormControl>
                            <Input 
                              type="date" 
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="dueDate"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Due Date</FormLabel>
                          <FormControl>
                            <Input 
                              type="date" 
                              min={format(new Date(), 'yyyy-MM-dd')}
                              {...field} 
                            />
                          </FormControl>
                          <FormDescription>
                            Due date cannot be in the past
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base font-medium">Invoice Items</h3>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => append({
                          productId: '',
                          description: '',
                          quantity: 1,
                          unitPrice: 0,
                          taxRate: 0
                        })}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Item
                      </Button>
                    </div>

                    {fields.map((field, index) => (
                      <div key={field.id} className="border rounded-md p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium">Item {index + 1}</h4>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => remove(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name={`items.${index}.productId`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Product/Service</FormLabel>
                                <Select
                                  onValueChange={(value) => {
                                    field.onChange(value);
                                    const product = products?.find(p => p.id.toString() === value);
                                    if (product) {
                                      form.setValue(`items.${index}.description`, product.description || '');
                                      form.setValue(`items.${index}.unitPrice`, Number(product.price));
                                    }
                                  }}
                                  defaultValue={field.value?.toString()}
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select or leave blank" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="custom">Custom Item</SelectItem>
                                    {products?.map((product) => (
                                      <SelectItem key={product.id} value={product.id.toString()}>
                                        {product.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name={`items.${index}.description`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Description</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                          <FormField
                            control={form.control}
                            name={`items.${index}.quantity`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Quantity</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                    value={field.value}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name={`items.${index}.unitPrice`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Unit Price</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                    value={field.value}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name={`items.${index}.taxRate`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Tax Rate (%)</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                    value={field.value}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="notes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Notes</FormLabel>
                          <FormControl>
                            <Textarea {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="terms"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Terms & Conditions</FormLabel>
                          <FormControl>
                            <Textarea {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <DialogFooter>
                    <div className="flex flex-wrap gap-2 justify-end">
                      <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={updateInvoiceMutation.isPending}
                      >
                        {updateInvoiceMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Save Changes
                      </Button>
                      {selectedInvoice.status === 'draft' && (
                        <Button 
                          type="button" 
                          variant="default" 
                          disabled={updateInvoiceMutation.isPending}
                          onClick={async () => {
                            // Validate form first
                            const isValid = await form.trigger();
                            if (isValid) {
                              const values = form.getValues();
                              // Update the form values first
                              await handleEditInvoice(values);
                              // Then update the status to sent
                              updateInvoiceStatusMutation.mutate({
                                id: selectedInvoice.id,
                                status: 'sent'
                              }, {
                                onSuccess: () => {
                                  toast({
                                    title: "Success",
                                    description: "Invoice sent successfully",
                                  });
                                  setIsEditDialogOpen(false);
                                }
                              });
                            }
                          }}
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Send Invoice
                        </Button>
                      )}
                    </div>
                  </DialogFooter>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}
